// Compact "Add Purchase Request" popup used from the Coach Dashboard's
// Purchase tab. Deliberately narrow/single-column (unlike the large
// admin CreatePurchaseRequestModal) and only asks for the fields coaches
// actually need: Balagruha, Priority, Due Date, Products, Notes, Attachments.
// Pass `request` to edit an existing (pending) purchase request with the
// same fields; without it the popup creates a new one as before. The person
// who created the request also gets a Status field there to mark it Delivered
// to Balagruha; a purchase manager also gets "Delivering to <requester>".
import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Paperclip, X } from 'lucide-react';
import {
    createPurchaseRequest,
    updatePurchaseRequest,
    updatePurchaseRequestStatus,
    deletePurchaseRequestAttachment,
    getAllShopItems,
} from '../../../api';
import { deliveringLabel, markDelivering } from './purchaseDelivery';
import showToast from '../../../utils/toast';
import './AddPurchaseRequestModal.css';
import SearchableSelect from '../../common/SearchableSelect';

const VALID_CATEGORIES = ['ISF Shop', 'Medicines', 'Consumables', 'Repairs', 'Infra', 'Others'];
const deriveCategory = (product) =>
    VALID_CATEGORIES.includes(product?.category) ? product.category : 'ISF Shop';

const idOf = (value) => (value?._id || value || '').toString();

// Edit only, and only for the request's creator (an admin editing someone
// else's request doesn't get it): the backend lets just them move a pending
// request straight to 'delivered_balagruha'.
const EDIT_STATUS_CHOICES = [
    { value: 'pending', label: 'Pending' },
    { value: 'delivered_balagruha', label: 'Delivered to Balagruha' },
];

// Purchase manager only (`canMarkDelivering`): the request goes on to its
// requester, see purchaseDelivery.js.
const DELIVERING = 'delivering';

const isRequestCreator = (request) => {
    const userId = localStorage.getItem('userId');
    return !!userId && idOf(request?.requestedBy) === userId;
};

// Deadlines are saved as local midnight, so read them back in local time
// (toISOString would shift them a day back in IST).
const toDateInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Saved request item -> the shape this form edits. Manual entries have no productId.
const toFormItem = (entry, index) => {
    const productId = entry.productId?._id || entry.productId || null;
    return {
        key: productId ? productId.toString() : `manual-${index}`,
        productId,
        productName: entry.productName || entry.productId?.name || entry.name || '',
        productSKU: entry.productSKU || entry.productId?.sku,
        requestedQuantity: entry.requestedQuantity || entry.quantity || 1,
        // Kept so an edit doesn't wipe costs the purchase manager entered
        estimatedUnitCost: entry.estimatedUnitCost,
        isManualEntry: !productId || undefined,
    };
};

const PRIORITIES = [
    { value: 'high', label: 'High', color: '#dc2626' },
    { value: 'medium', label: 'Medium', color: '#d97706' },
    { value: 'low', label: 'Low', color: '#2563eb' },
];

export default function AddPurchaseRequestModal({
    balagruhas = [],
    request = null,
    onClose,
    onCreated = () => {},
    onUpdated = () => {},
    canMarkDelivering = false,
}) {
    const isEdit = !!request;
    const originalDueDate = toDateInput(request?.deadline);
    const [balagruhaId, setBalagruhaId] = useState(
        isEdit ? idOf(request.balagruhaId) : balagruhas.length === 1 ? balagruhas[0]._id : ''
    );
    const [category, setCategory] = useState(request?.category || '');
    const [priority, setPriority] = useState(request?.priority || 'medium');
    const [dueDate, setDueDate] = useState(originalDueDate);
    const [products, setProducts] = useState([]);
    const [fetchingProducts, setFetchingProducts] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState(() => (request?.items || []).map(toFormItem));
    const [notes, setNotes] = useState(request?.justification || '');
    const [attachments, setAttachments] = useState([]);
    // Edit only: files already on the request; removals are applied on Save.
    const [existingAttachments, setExistingAttachments] = useState(request?.attachments || []);
    const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
    const canMarkDelivered = isEdit && isRequestCreator(request);
    const statusChoices = [
        EDIT_STATUS_CHOICES[0],
        ...(isEdit && canMarkDelivering ? [{ value: DELIVERING, label: deliveringLabel(request) }] : []),
        ...(canMarkDelivered ? EDIT_STATUS_CHOICES.slice(1) : []),
    ];
    const showsStatus = statusChoices.length > 1;
    const [status, setStatus] = useState('pending');
    const statusLabel = statusChoices.find((choice) => choice.value === status)?.label || status;
    const [submitting, setSubmitting] = useState(false);
    const pickerRef = useRef(null);

    const todayStr = new Date().toISOString().split('T')[0];
    // An edited request keeps its saved due date even if that date has passed.
    const minDueDate = isEdit && originalDueDate && originalDueDate < todayStr ? originalDueDate : todayStr;

    // The request's own Balagruha may not be in this user's list (e.g. STOCK).
    const requestBalagruhaId = isEdit ? idOf(request.balagruhaId) : '';
    const balagruhaOptions =
        requestBalagruhaId && !balagruhas.some((bg) => bg._id === requestBalagruhaId)
            ? [
                  ...balagruhas,
                  {
                      _id: requestBalagruhaId,
                      name:
                          request.balagruhaId?.name ||
                          request.balagruhaName ||
                          (requestBalagruhaId === 'STOCK' ? 'Stock (General Inventory)' : 'Current Balagruha'),
                  },
              ]
            : balagruhas;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!balagruhaId) {
            setProducts([]);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setFetchingProducts(true);
                const response = await getAllShopItems();
                if (cancelled) return;
                if (response.success) {
                    const scoped = (response.data || []).filter(
                        (product) => !product.balagruhaId || product.balagruhaId === balagruhaId
                    );
                    setProducts(scoped);
                }
            } catch (error) {
                if (!cancelled) showToast('Failed to load products', 'error');
            } finally {
                if (!cancelled) setFetchingProducts(false);
            }
        })();

        return () => { cancelled = true; };
    }, [balagruhaId]);

    const handleBalagruhaChange = (e) => {
        setBalagruhaId(e.target.value);
        setItems([]);
        setPickerOpen(false);
    };

    const addProduct = (product) => {
        if (items.some((item) => item.productId === product._id)) {
            setPickerOpen(false);
            setSearch('');
            return;
        }

        setItems((prev) => [
            ...prev,
            {
                key: product._id,
                productId: product._id,
                productName: product.name,
                productSKU: product.sku,
                requestedQuantity: 1,
                category: deriveCategory(product),
            },
        ]);
        setPickerOpen(false);
        setSearch('');
    };

    const removeProduct = (key) => {
        setItems((prev) => prev.filter((item) => item.key !== key));
    };

    const updateQuantity = (key, value) => {
        const quantity = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
        setItems((prev) =>
            prev.map((item) => (item.key === key ? { ...item, requestedQuantity: quantity } : item))
        );
    };

    const removeExistingAttachment = (attachmentId) => {
        setExistingAttachments((prev) => prev.filter((file) => file._id !== attachmentId));
        setRemovedAttachmentIds((prev) => [...prev, attachmentId]);
    };

    const handleFileChange = (e) => {
        setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const filteredProducts = products.filter((product) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return product.name?.toLowerCase().includes(q) || product.sku?.toLowerCase().includes(q);
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!balagruhaId) {
            showToast('Please select a Balagruha', 'error');
            return;
        }

        if (!category) {
            showToast('Please select a category', 'error');
            return;
        }

        if (!dueDate) {
            showToast('Please select a due date', 'error');
            return;
        }

        if (dueDate < todayStr && !(isEdit && dueDate === originalDueDate)) {
            showToast('Due date cannot be in the past', 'error');
            return;
        }

        if (items.length === 0) {
            showToast('Please select at least one product', 'error');
            return;
        }

        if (items.some((item) => !item.requestedQuantity || item.requestedQuantity < 1)) {
            showToast('Please enter a valid quantity (>= 1) for every product', 'error');
            return;
        }

        try {
            setSubmitting(true);

            const submitData = new FormData();
            submitData.append('balagruhaId', balagruhaId);
            submitData.append('category', category);
            submitData.append('priority', priority);
            submitData.append('deadline', dueDate);
            submitData.append(
                'items',
                JSON.stringify(
                    // estimatedUnitCost / isManualEntry only exist on items of an
                    // edited request; JSON drops them while undefined.
                    items.map(({ productId, productName, productSKU, requestedQuantity, estimatedUnitCost, isManualEntry }) => ({
                        productId,
                        productName,
                        productSKU,
                        requestedQuantity,
                        estimatedUnitCost,
                        isManualEntry,
                    }))
                )
            );
            // On edit always send notes, so clearing them is saved too
            if (isEdit || notes.trim()) submitData.append('justification', notes.trim());
            attachments.forEach((file) => submitData.append('attachments', file));

            if (isEdit) {
                const response = await updatePurchaseRequest(request._id, submitData);
                if (!response.success) {
                    showToast(response.message || 'Failed to update purchase request', 'error');
                    return;
                }
                const removals = await Promise.allSettled(
                    removedAttachmentIds.map((attachmentId) => deletePurchaseRequestAttachment(request._id, attachmentId))
                );
                // Status goes last: the edits above are only accepted while pending.
                let statusError = null;
                if (status === DELIVERING) {
                    const result = await markDelivering(request);
                    if (!result.success) {
                        statusError = result.changed ? `it was marked Ordered only (${result.message})` : result.message;
                    }
                } else if (canMarkDelivered && status !== 'pending') {
                    try {
                        const statusResponse = await updatePurchaseRequestStatus(request._id, {
                            status,
                            notes: 'Marked Delivered to Balagruha by requester',
                        });
                        if (!statusResponse?.success) statusError = statusResponse?.message || 'Failed to update status';
                    } catch (error) {
                        statusError = error.response?.data?.message || 'Failed to update status';
                    }
                }
                if (statusError) {
                    showToast(`Purchase request updated, but it could not be marked ${statusLabel}: ${statusError}`, 'error');
                } else if (removals.some((result) => result.status === 'rejected')) {
                    showToast('Purchase request updated, but some attachments could not be removed', 'error');
                } else if (showsStatus && status !== 'pending') {
                    showToast(`Purchase request updated and marked ${statusLabel}`, 'success');
                } else {
                    showToast('Purchase request updated successfully', 'success');
                }
                onUpdated();
                return;
            }

            const response = await createPurchaseRequest(submitData);

            if (response.success) {
                showToast('Purchase request created successfully', 'success');
                onCreated();
            } else {
                showToast(response.message || 'Failed to create purchase request', 'error');
            }
        } catch (error) {
            showToast(
                error.response?.data?.message || (isEdit ? 'Failed to update purchase request' : 'Failed to create purchase request'),
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="apr-overlay" onClick={onClose}>
            <div className="apr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="apr-header">
                    <h3>{isEdit ? 'Edit Purchase Request' : 'Add Purchase Request'}</h3>
                    <button type="button" className="apr-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="apr-body">
                        <div className="apr-field">
                            <label>Balagruha <span className="apr-required">*</span></label>
                            <SearchableSelect searchPlaceholder="Search balagruha..." value={balagruhaId} onChange={handleBalagruhaChange} required>
                                <option value="">Select Balagruha</option>
                                {balagruhaOptions.map((bg) => (
                                    <option key={bg._id} value={bg._id}>{bg.name}</option>
                                ))}
                            </SearchableSelect>
                        </div>

                        <div className="apr-field">
                            <label>Priority <span className="apr-required">*</span></label>
                            <div className="apr-priority-row">
                                {PRIORITIES.map((p) => (
                                    <button
                                        key={p.value}
                                        type="button"
                                        className={`apr-priority-pill ${priority === p.value ? 'active' : ''}`}
                                        style={priority === p.value ? { background: p.color, borderColor: p.color } : { color: p.color, borderColor: p.color }}
                                        onClick={() => setPriority(p.value)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <small className="apr-hint">Item name will be shown in this colour in the list.</small>
                        </div>

                        <div className="apr-field">
                            <label>Category <span className="apr-required">*</span></label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} required>
                                <option value="">Select Category</option>
                                {VALID_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat} disabled={cat === 'Repairs'}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                            <small className="apr-hint">Repairs must be requested from Repair Management, not here.</small>
                        </div>

                        <div className="apr-field">
                            <label>Due Date <span className="apr-required">*</span></label>
                            <input
                                type="date"
                                value={dueDate}
                                min={minDueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                required
                            />
                            <small className="apr-hint">Due date must be today or a future date.</small>
                        </div>

                        <div className="apr-field" ref={pickerRef}>
                            <label>Select Products <span className="apr-required">*</span></label>

                            <div className="apr-picker">
                                <button
                                    type="button"
                                    className="apr-picker-trigger"
                                    disabled={!balagruhaId || fetchingProducts}
                                    onClick={() => setPickerOpen((prev) => !prev)}
                                >
                                    {fetchingProducts
                                        ? 'Loading products...'
                                        : !balagruhaId
                                        ? 'Select a Balagruha first'
                                        : 'Search and select products from ISF Shop...'}
                                </button>

                                {pickerOpen && balagruhaId && !fetchingProducts && (
                                    <div className="apr-picker-panel">
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Search products..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            className="apr-picker-search"
                                        />
                                        <div className="apr-picker-options">
                                            {filteredProducts.length === 0 ? (
                                                <div className="apr-picker-empty">No products found</div>
                                            ) : (
                                                filteredProducts.map((product) => (
                                                    <button
                                                        type="button"
                                                        key={product._id}
                                                        className="apr-picker-option"
                                                        onClick={() => addProduct(product)}
                                                    >
                                                        {product.name}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <small className="apr-hint">
                                Need a new item? Contact an Admin to add it to the Master Catalog.
                            </small>
                        </div>

                        {items.length > 0 && (
                            <div className="apr-field">
                                <label>Selected Products</label>
                                <div className="apr-selected-list">
                                    {items.map((item) => (
                                        <div className="apr-selected-row" key={item.key}>
                                            <button
                                                type="button"
                                                className="apr-remove-btn"
                                                onClick={() => removeProduct(item.key)}
                                                aria-label={`Remove ${item.productName}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <span className="apr-selected-name">{item.productName}</span>
                                            <div className="apr-qty-field">
                                                <label>Quantity <span className="apr-required">*</span></label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.requestedQuantity}
                                                    onChange={(e) => updateQuantity(item.key, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {balagruhaId && (
                            <button
                                type="button"
                                className="apr-add-another"
                                onClick={() => setPickerOpen(true)}
                            >
                                + Add Another Product
                            </button>
                        )}

                        <div className="apr-field">
                            <label>Notes (Optional)</label>
                            <textarea
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add any additional notes..."
                                maxLength={500}
                            />
                        </div>

                        <div className="apr-field">
                            <label>Attachments (Optional)</label>
                            <label className="apr-file-btn">
                                <Paperclip size={14} />
                                Choose Files
                                <input type="file" multiple onChange={handleFileChange} hidden />
                            </label>

                            {existingAttachments.length > 0 && (
                                <div className="apr-file-list">
                                    {existingAttachments.map((file) => (
                                        <div className="apr-file-chip" key={file._id} title={file.filename}>
                                            <Paperclip size={11} />
                                            <span>{file.filename || 'Attachment'}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeExistingAttachment(file._id)}
                                                aria-label={`Remove ${file.filename || 'attachment'}`}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {attachments.length > 0 && (
                                <div className="apr-file-list">
                                    {attachments.map((file, index) => (
                                        <div className="apr-file-chip" key={`${file.name}-${index}`}>
                                            <span>{file.name}</span>
                                            <button type="button" onClick={() => removeAttachment(index)} aria-label="Remove file">
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {showsStatus && (
                            <div className="apr-field">
                                <label htmlFor="apr-status">Status</label>
                                <select id="apr-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                                    {statusChoices.map((choice) => (
                                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                                    ))}
                                </select>
                                <small className="apr-hint">
                                    {status !== 'pending'
                                        ? `On Save the request is marked ${statusLabel} and can no longer be edited.`
                                        : 'Only you, as the creator of this request, can change its status.'}
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="apr-footer">
                        <button type="button" className="apr-btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="apr-btn-submit" disabled={submitting}>
                            {isEdit
                                ? submitting ? 'Saving...' : 'Save Changes'
                                : submitting ? 'Adding...' : 'Add Purchase Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
